const debounce = require('lodash/debounce');
const isMatchedBreakpoint = require('../common/utils/isMatchedBreakpoint');

const bottomSheetLimitBreakpoint = 'bp_1025';
const debounceDelay = 500;

let defaultSelectors = {
  bottomSheet: '.bottom-sheet',
  bottomSheetInitialized: 'bottom-sheet--initialized',
  bottomSheetFull: 'bottom-sheet--fullscreen',
  bottomSheetDragging: 'bottom-sheet--dragging',
  bottomSheetInteraction: 'interaction--bounce-top',
  bottomSheetHeader: '.bottom-sheet__header',
  bottomSheetInfo: 'bottom-sheet__info',
  bodyScrollDisabled: 'u-scroll-disabled',
};
let sheetHeight = 20; /* Sheet initial height on vh */
let sheetInitialHeight = 0; /* Sheet initial height on vh including or not button */
let dragPosition;
let sheet = document.querySelector(defaultSelectors.bottomSheet);
let sheetHeader = sheet?.querySelector(defaultSelectors.bottomSheetHeader);
const body = document.querySelector('body');

/**
 * Calculate sheet visible height
 * @param value
 */
const setSheetHeight = (value) => {
  sheetHeight = Math.max(0, Math.min(100, value));
  sheet.style.transform = `translateY(-${sheetHeight}%)`;

  if (sheetHeight >= 30) {
    sheet.classList.add(defaultSelectors.bottomSheetFull);
  } else {
    sheet.classList.remove(defaultSelectors.bottomSheetFull);
  }
};

/**
 * Calculate sheet initial visible height
 */
const setInitialSheetHeight = (height) => {
  const sheetHeaderHeight = height || sheetHeader.clientHeight;
  const sheetComputedStyles = getComputedStyle(sheet);
  const visibleHeaderSize = sheetHeaderHeight + parseInt(sheetComputedStyles.paddingTop, 10);
  sheet.style.transform = `translateY(-${visibleHeaderSize}px)`;
};

const touchPosition = (event) =>
  (event.touches ? event.touches[0] : event);

const onDragStart = (event) => {
  if (event.target.tagName !== 'BUTTON') {
    dragPosition = touchPosition(event).pageY;
    sheet.classList.add(defaultSelectors.bottomSheetDragging);
    body.classList.add(defaultSelectors.bodyScrollDisabled);
  } else {
    dragPosition = undefined;
  }
};

const onDragMove = (event) => {
  if (dragPosition === undefined) return;

  const y = touchPosition(event).pageY;
  const deltaY = dragPosition - y;
  const deltaHeight = (deltaY / window.innerHeight) * 100;

  setSheetHeight(sheetHeight + deltaHeight);
  dragPosition = y;
};

/**
 * On drag end, if the movement exceeds 50% of the screen, the sheet must be at fullscreen
 */
const onDragEnd = () => {
  if (dragPosition) {
    sheet.classList.remove(defaultSelectors.bottomSheetDragging);
    if (sheetHeight > 50) {
      setSheetHeight(100);
    } else {
      // It is important to first remove classes & then set initial height to avoid unwanted calculation results
      sheet.classList.remove(defaultSelectors.bottomSheetFull);
      body.classList.remove(defaultSelectors.bodyScrollDisabled);
      setInitialSheetHeight();
    }
  }
  dragPosition = undefined;
};

/**
 * On click on sheet, the sheet should be opened
 */
function onClick(e) {
  const isFullScreen = sheet.classList.contains(defaultSelectors.bottomSheetFull);
  if (isFullScreen) {
    const isBottomSheetInfo = e.target.classList.contains(defaultSelectors.bottomSheetInfo);
    if (isBottomSheetInfo) {
      setInitialSheetHeight(sheetInitialHeight);
      sheet.classList.remove(defaultSelectors.bottomSheetFull);
      body.classList.remove(defaultSelectors.bodyScrollDisabled);
    }
  } else {
    setSheetHeight(100);
  }
}

const addListeners = () => {
  sheet.addEventListener('mousedown', onDragStart);
  sheet.addEventListener('touchstart', onDragStart);
  sheet.addEventListener('click', onClick);

  window.addEventListener('mousemove', onDragMove);
  window.addEventListener('touchmove', onDragMove);

  window.addEventListener('mouseup', onDragEnd);
  window.addEventListener('touchend', onDragEnd);
};

const removeListeners = () => {
  sheet.removeEventListener('mousedown', onDragStart);
  sheet.removeEventListener('touchstart', onDragStart);
  sheet.removeEventListener('click', onClick);

  window.removeEventListener('mousemove', onDragMove);
  window.removeEventListener('touchmove', onDragMove);

  window.removeEventListener('mouseup', onDragEnd);
  window.removeEventListener('touchend', onDragEnd);
};

const create = () => {
  sheet.classList.add(defaultSelectors.bottomSheetInitialized);
  sheetInitialHeight = sheetHeader.clientHeight;
  setInitialSheetHeight();
  addListeners();
  sheet.classList.add(defaultSelectors.bottomSheetInteraction);
};

const destroy = () => {
  sheet.classList.remove(defaultSelectors.bottomSheetInitialized);
  setSheetHeight(0);
  removeListeners();
};

const initialize = (selectors) => {
  defaultSelectors = { ...defaultSelectors, ...selectors };
  sheet = document.querySelector(defaultSelectors.bottomSheet);
  sheetHeader = document.querySelector(defaultSelectors.bottomSheetHeader);

  if (sheet && sheetHeader) {
    if (isMatchedBreakpoint.matches(bottomSheetLimitBreakpoint)) {
      create();
    }

    // eslint-disable-next-line listeners/no-missing-remove-event-listener
    window.addEventListener('resize', debounce(() => {
      if (isMatchedBreakpoint.matches(bottomSheetLimitBreakpoint)) {
        if (!sheet.classList.contains(defaultSelectors.bottomSheetInitialized)) {
          create();
        }
      } else if (sheet.classList.contains(defaultSelectors.bottomSheetInitialized)) {
        destroy();
      }
    }, debounceDelay));

    // eslint-disable-next-line listeners/no-missing-remove-event-listener, listeners/no-inline-function-event-listener
    document.addEventListener('checkout-stage:changed', () => {
      // It is important to first remove fullpage class & then set initial height to avoid unwanted calculation results
      const isFullScreen = sheet.classList.contains(defaultSelectors.bottomSheetFull);
      if (isFullScreen) {
        sheet.classList.remove(defaultSelectors.bottomSheetFull);
        body.classList.remove(defaultSelectors.bodyScrollDisabled);
      }

      sheetInitialHeight = sheetHeader.clientHeight;
      setInitialSheetHeight(sheetInitialHeight);
    });
  }
};

module.exports = {
  initialize,
};
